// @flow
import React, { useContext } from 'react';

import { COLORS } from 'Components/utils/color/colors';
import ReactAppContext from 'Context/ReactAppContext';
import { getCurrencySymbol } from 'Components/utils/price/price.util';

import { Badge } from 'Components/frameworks/badge/Badge.jsx';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Title } from 'Components/frameworks/title/Title.jsx';
import { t, getKeyValue } from 'Src/i18n';

import {
  ProductFeatureDetailServiceItem,
  type ProductFeatureDetailServiceItemProps,
} from './ProductFeatureDetailServiceItem.jsx';

export type ProductFeatureDetailServiceProps = {
  badges?: Array<{
    label?: string,
    iconName: IconName,
  }>,
  className?: string,
  color?: Colors,
  duration?: {
    description: string,
    label?: string,
  },
  id?: string,
  items?: Array<ProductFeatureDetailServiceItemProps>,
  level?: string,
  note?: string,
  targetPrice?: {
    currency: string,
    label: string,
    price: number,
  },
  title: string,
};

export const ProductFeatureDetailService = (props: ProductFeatureDetailServiceProps) => {
  const {
    badges = [],
    className,
    color = COLORS.GRAY_DARKER,
    duration,
    id,
    items = [],
    level,
    note,
    targetPrice = {},
    title,
  } = props;

  const { locale } = useContext(ReactAppContext);

  if (!title) {
    return null;
  }

  const instalmentValue = getKeyValue('common.price.instalmentValue');

  return (
    <article className={className} data-id={id}>
      <Title
        className="sm:text-lg justify-between items-baseline"
        color={color}
        hLevel={2}
        theme="title-sans-3"
      >
        {title}
      </Title>
      <Title className="sm:text-md" hLevel={3} theme="title-sans-1">
        {level}
      </Title>
      {targetPrice?.price && (
        <TemplateString
          className="block text-base py-2"
          template={targetPrice.label}
          values={{
            currency: targetPrice.currency ? getCurrencySymbol(locale, targetPrice.currency) : '',
            price: getKeyValue('common.price.displayInstalmentsPrice')
              ? t('common.price.instalmentPrice', {
                  instalmentValue,
                  dividedPrice: Math.trunc(targetPrice.price / instalmentValue),
                })
              : Math.trunc(targetPrice.price) || '',
          }}
          valuesClassName={{
            currency: `text-xl text-${color} font-bold`,
            price: `text-xl text-${color} font-bold`,
          }}
        />
      )}
      {duration?.description && (
        <div className="text-1 my-3">
          {duration.label ? <span className="font-bold">{`${duration.label}: `}</span> : null}
          {duration.description}
        </div>
      )}
      {badges.map(({ iconName, label }) => (
        <div className="flex items-center my-3" key={iconName}>
          <Badge
            className="rtl:mr-2"
            color={COLORS.WHITE}
            colorBg={COLORS.BLUE}
            colorBorder={COLORS.BLUE}
            iconName={iconName}
            iconSize="2.375rem"
            size={50}
            variant="pill"
          />
          <span className="font-brand text-blue text-md">{label}</span>
        </div>
      ))}
      {!!items.length && (
        <ul className="list-none p-0 leading-normal text-base text-gray-darker">
          {items.map(item => (
            <ProductFeatureDetailServiceItem
              className="my-1 py-2 list-disc"
              description={item.description}
              items={item.items}
              key={item.description}
              label={item.label}
              note={item.note}
              tag={item.tag}
            />
          ))}
        </ul>
      )}
      {!!note && <div className="italic mt-4 text-sm">{note}</div>}
    </article>
  );
};
